import React, { useMemo } from 'react';

// Deliberately a standalone copy of the grid-geometry logic in
// FacultySchedulePage.js, not a shared import — that page is out of scope
// this session, and importing from it would mean editing it. This
// component is read-only throughout: no onClick anywhere, no cursor-pointer
// styling, no selection state, no form wiring of any kind.

const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const START_HOUR = 9;
const SLOT_COUNT = 24; // 09:00–21:00 in 30-minute slots

const timeToSlotIndex = (time) => {
  const [h, m] = time.split(':').map(Number);
  return (h * 60 + m - START_HOUR * 60) / 30;
};

const formatSlotLabel = (index) => {
  const totalMinutes = START_HOUR * 60 + index * 30;
  const h24 = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const period = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}${m === 0 ? '' : ':' + String(m).padStart(2, '0')} ${period}`;
};

const CAMPUS_LABEL = { ardee: 'Ardee City', arcadia: 'Arcadia' };

export default function ReadOnlyScheduleGrid({ blocks, emptyMessage }) {
  const coveredSlots = useMemo(() => {
    const set = new Set();
    for (const block of blocks) {
      const start = timeToSlotIndex(block.start_time);
      const end = timeToSlotIndex(block.end_time);
      for (let i = start; i < end; i++) {
        set.add(`${block.day_of_week}|${i}`);
      }
    }
    return set;
  }, [blocks]);

  if (blocks.length === 0 && emptyMessage) {
    return <div className="py-16 text-center text-slate-400 text-sm">{emptyMessage}</div>;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
      <div
        className="grid min-w-[760px]"
        style={{
          gridTemplateColumns: '72px repeat(7, minmax(0, 1fr))',
          gridTemplateRows: `36px repeat(${SLOT_COUNT}, 32px)`,
        }}
        data-testid="admin-schedule-grid"
      >
        {/* Header row */}
        <div
          className="bg-slate-50 dark:bg-slate-900 border-b border-r border-slate-200 dark:border-slate-700"
          style={{ gridColumn: 1, gridRow: 1 }}
        />
        {DAYS.map((day, dayIdx) => (
          <div
            key={day.key}
            className="flex items-center justify-center text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 border-b border-l border-slate-200 dark:border-slate-700"
            style={{ gridColumn: dayIdx + 2, gridRow: 1 }}
            data-testid={`admin-day-header-${day.key}`}
          >
            {day.label}
          </div>
        ))}

        {/* Hour labels, one per 2 slots */}
        {Array.from({ length: SLOT_COUNT }).map((_, slotIdx) =>
          slotIdx % 2 === 0 ? (
            <div
              key={`time-${slotIdx}`}
              className="bg-slate-50 dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 text-[11px] text-slate-400 dark:text-slate-500 pl-1.5 pt-0.5"
              style={{ gridColumn: 1, gridRow: `${slotIdx + 2} / span 2` }}
            >
              {formatSlotLabel(slotIdx)}
            </div>
          ) : null
        )}

        {/* Empty (non-interactive, decorative) grid cells */}
        {DAYS.map((day, dayIdx) =>
          Array.from({ length: SLOT_COUNT }).map((_, slotIdx) => {
            const key = `${day.key}|${slotIdx}`;
            if (coveredSlots.has(key)) return null;
            return (
              <div
                key={`cell-${key}`}
                className="border-b border-l border-slate-100 dark:border-slate-700/60"
                style={{ gridColumn: dayIdx + 2, gridRow: slotIdx + 2 }}
              />
            );
          })
        )}

        {/* Existing blocks, rendered as one merged cell each — read-only,
            no onClick anywhere on this component. */}
        {blocks.map((block) => {
          const dayIdx = DAYS.findIndex((d) => d.key === block.day_of_week);
          if (dayIdx === -1) return null;
          const start = timeToSlotIndex(block.start_time);
          const end = timeToSlotIndex(block.end_time);
          return (
            <div
              key={block.id}
              className="m-0.5 rounded-md bg-primary/90 text-white px-2 py-1 text-[11px] leading-tight overflow-hidden flex flex-col justify-center"
              style={{ gridColumn: dayIdx + 2, gridRow: `${start + 2} / ${end + 2}` }}
              data-testid={`admin-block-${block.id}`}
            >
              <div className="font-semibold truncate">{block.group_name}</div>
              <div className="truncate">{block.subject}</div>
              <div className="truncate text-white/80">{CAMPUS_LABEL[block.campus] || block.campus}</div>
              <div className="truncate text-white/80">
                {block.start_time}–{block.end_time}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
